import { useCallback, useEffect, useRef, useState } from 'react';
import { ConsentV2Options, CustomIdentifiers } from '../domain/clarity.types';
import { getEnvironmentVariable } from "@utils/shared.environment.utils.ts";

interface UseClarityOptions {
  autoInit?: boolean; // Auto-initialize based on stored consent (default: true)
}

export function useClarity({autoInit = true}: UseClarityOptions = {}) {
  const scriptLoaded = useRef(false);
  const consentApplied = useRef(false);
  const projectId = getEnvironmentVariable('VITE_CLARITY_PROJECT_ID');
  const [consent, setConsent] = useState<ConsentV2Options | null>(null);

  // Load Clarity script
  const loadScript = useCallback(() => {
    if (scriptLoaded.current || window.clarityLoaded) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${projectId}");
    `;
    document.head.appendChild(script);
    scriptLoaded.current = true;
    window.clarityLoaded = true;
  }, [projectId]);

  // Auto-initialize based on stored consent
  useEffect(() => {
    if (!autoInit || consentApplied.current) return;

    const stored = localStorage.getItem('clarity_consent_v2');

    if (stored) {
      const storedConsent = JSON.parse(stored);
      setConsent(storedConsent);

      // Only load script if analytics consent is granted
      if (storedConsent.analytics_Storage === 'granted') {
        loadScript();
        setTimeout(() => {
          window.clarity?.('consentv2', {
            analytics_Storage: storedConsent.analytics_Storage
          });
        }, 100);
      }
      consentApplied.current = true;
    }
    // If no stored consent, leave consent as null to show banner
  }, [autoInit, loadScript]);

  // Clear Clarity cookies and consent preference
  const clearClarityCookies = useCallback(() => {
    // Clear _clck and _clsk cookies
    document.cookie = '_clck=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = '_clsk=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }, []);

  // Grant consent with ConsentV2
  const grantConsent = useCallback(async (options: ConsentV2Options) => {
    // Only load clarity script if analytics consent is granted
    if (options.analytics_Storage === 'granted') {
      loadScript();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.clarity?.('consentv2', options);

      // Store consent preference only when granting
      localStorage.setItem('clarity_consent_v2', JSON.stringify({
        ...options,
        timestamp: new Date().toISOString()
      }));
    } else {
      // If denied, revoke everything (clear cookies AND preference)
      clearClarityCookies();
      localStorage.removeItem('clarity_consent_v2');
    }

    setConsent(options);
    consentApplied.current = true;
  }, [loadScript, clearClarityCookies]);

  // Grant analytics consent
  const grantAnalytics = useCallback(() => {
    return grantConsent({
      analytics_Storage: 'granted'
    });
  }, [grantConsent]);

  // Deny consent (no-consent mode)
  const denyConsent = useCallback(async () => {
    // Clear everything - cookies and stored preference
    clearClarityCookies();
    localStorage.removeItem('clarity_consent_v2');

    // Set state to null so banner shows on next visit
    setConsent(null);
    consentApplied.current = false;
  }, [clearClarityCookies]);

  // Track event
  const trackEvent = useCallback((eventName: string) => {
    window.clarity?.('event', eventName);
  }, []);

  // Set custom tag
  const setTag = useCallback((key: string, value: string | string[]) => {
    window.clarity?.('set', key, value);
  }, []);

  // Identify user
  const identify = useCallback((identifiers: CustomIdentifiers) => {
    window.clarity?.(
      'identify',
      identifiers.customId,
      identifiers.customSessionId,
      identifiers.customPageId,
      identifiers.friendlyName
    );
  }, []);

  // Upgrade session
  const upgradeSession = useCallback((reason: string) => {
    window.clarity?.('upgrade', reason);
  }, []);

  return {
    consent,
    grantConsent,
    grantAnalytics,
    denyConsent,
    trackEvent,
    setTag,
    identify,
    upgradeSession,
  };
}